
import { defineComponent, type PropType } from 'vue'

import AddRepositoriesToGroup from '~/apollo/mutations/group/AddRepositoriesToGroup.gql'
import RepoSearchQuery from '~/apollo/queries/repository/searchList.gql'

import { setAllCacheBustKeys } from '~/utils/repositoryGroups'

import type { ApolloQueryResult } from 'apollo-client'
import type { FetchResult } from 'apollo-link'
import type { ChipOption } from '~/components/Common/UI2/ChipInput/UIChipInput.vue'
import {
  AddRepositoriesToGroupMutation,
  RepositoryKindChoices,
  RepositorySearchListQuery
} from '~/types/types'

export default defineComponent({
  name: 'AddRepositoriesToGroupModal',
  props: {
    group: {
      type: String,
      required: true
    },
    reposAlreadyAdded: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    groupSlug: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      repoSearchString: '',
      reposToAdd: [] as ChipOption[],
      submitting: false,
      isChipInputOpen: false,
      searchResults: [] as ChipOption[]
    }
  },
  computed: {
    isValid() {
      return this.reposToAdd.length > 0
    },
    preventClose() {
      return this.submitting || this.isChipInputOpen
    }
  },
  methods: {
    closeModal() {
      this.$emit('close')
    },
    handleChipInputClose(value: boolean) {
      this.isChipInputOpen = value
    },
    async handleRepoSearch(searchString: string) {
      this.repoSearchString = searchString
      try {
        const { owner: login, provider } = this.$route.params

        if (!login || !provider) {
          throw new Error('Missing required parameters')
        }

        const response: ApolloQueryResult<RepositorySearchListQuery> = await this.$fetchGraphqlData(
          RepoSearchQuery,
          {
            login,
            provider: this.$providerMetaMap[provider].value,
            query: searchString,
            kindIn: [RepositoryKindChoices.Repo, RepositoryKindChoices.Subrepo],
            limit: 10
          }
        )

        if (response.data) {
          const allResults =
            response.data.owner?.repositories?.edges?.map((repoEdge) => ({
              label: repoEdge?.node?.displayName ?? '',
              value: {
                id: repoEdge?.node?.id ?? ''
              }
            })) || []

          // Filter out repositories that are already selected or in the group
          const excludedRepoIds = new Set([
            ...this.reposAlreadyAdded,
            ...this.reposToAdd.map((repo) => repo.value.id)
          ])
          this.searchResults = allResults.filter((repo) => !excludedRepoIds.has(repo.value.id))
        }
      } catch (error) {
        this.$logErrorAndToast(error as Error, 'Failed to fetch list of repositories.')
      }
    },
    async addReposToGroup() {
      if (!this.isValid) return
      this.submitting = true
      const { owner, provider } = this.$route.params

      try {
        const response: FetchResult<AddRepositoriesToGroupMutation> =
          await this.$applyGraphqlMutation(AddRepositoriesToGroup, {
            input: {
              repositoryGroupId: this.group,
              repositoryIds: this.reposToAdd.map((repo) => repo.value.id)
            }
          })
        if (response.data?.addRepositoriesToRepositoryGroup?.ok) {
          this.$toast.success('Repositories added successfully.')
          this.$emit('repos-added')
          setAllCacheBustKeys(provider, owner, this.groupSlug)
          this.submitting = false
          this.isChipInputOpen = false
          this.closeModal()
        } else {
          this.$logErrorAndToast(
            new Error(
              response.errors?.map((e) => e.message).join(', ') ??
                'API error or unsuccessful update.'
            ),
            'Failed to add repositories to group.'
          )
        }
      } catch (e) {
        this.$logErrorAndToast(e as Error, 'Failed to add repositories to group.')
      }

      this.submitting = false
    }
  },
  watch: {
    reposToAdd: {
      handler() {
        this.handleRepoSearch('')
      }
    }
  }
})
